import LoginApi from './loginApi';
import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Alert } from '../components/ui/alert';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Send, Mail, MessageSquare } from 'lucide-react';
import { validateEmail } from '../lib/validator';

const ForgetPasswordPage: React.FC = () => {
  const [message, setMessage] = useState<string>('');
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [email, setEmail] = useState('');
  const [loginId, setLoginId] = useState('');
  const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [sendMethod, setSendMethod] = useState<'email' | 'text' | null>(null);

  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEmail(e.target.value);
  };

  const handleLoginIdChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setLoginId(e.target.value);
  };

  const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setErrorMessage('');

    if (name === 'email' && value) {
      const emailResult = validateEmail(value);
      if (!emailResult.isValid) {
        setErrorMessage('Invalid email address format');
      } else {
        setEmail(emailResult.value);
      }
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setStatus('loading');
    setErrorMessage('');
    setMessage('');

    // Validation
    if (!email) {
      setErrorMessage('Please provide your email');
      setStatus('error');
      return;
    }

    if (!loginId) {
      setErrorMessage('Please provide your login ID');
      setStatus('error');
      return;
    }

    if (!sendMethod) {
      setErrorMessage('Please select a send method (Email or Text)');
      setStatus('error');
      return;
    }

    try {

      await LoginApi.resetPassword(loginId, email, sendMethod === 'text' ? 'sms' : sendMethod);
      setStatus('success');
      setMessage('A link for resetting your password has been sent out.');
    } catch (error) {
      setStatus('error');
      setErrorMessage(error instanceof Error ? error.message : 'An unknown error occurred');
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4 py-12">
      <div className="w-full max-w-md">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-foreground mb-2">Reset Password</h1>
          <p className="text-gray-600">Enter your details to receive a password reset link</p>
        </div>

        {/* Card Container */}
        <div className="bg-white rounded-2xl shadow-lg p-8">
          {/* Success Message */}
          {status === 'success' && message && (
            <Alert variant="success" className="mb-6">
              {message}
            </Alert>
          )}

          {/* Error Message */}
          {errorMessage && (
            <Alert variant="error" className="mb-6">
              {errorMessage}
            </Alert>
          )}

          {/* Form */}
          {!message ? (
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Login ID */}
              <div>
                <label htmlFor="loginId" className="block text-sm font-medium text-foreground mb-2">
                  Login ID <span className="text-red-500">*</span>
                </label>
                <Input
                  type="text"
                  id="loginId"
                  name="login_id"
                  value={loginId}
                  onChange={handleLoginIdChange}
                  required
                  placeholder="Enter your login ID"
                />
              </div>

              {/* Email */}
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-foreground mb-2">
                  Email Address <span className="text-red-500">*</span>
                </label>
                <Input
                  type="email"
                  id="email"
                  name="email"
                  value={email}
                  onChange={handleEmailChange}
                  onBlur={handleBlur}
                  required
                  placeholder="Enter your email address"
                />
              </div>

              {/* Send Method Selection */}
              <div className="space-y-4">
                <div>
                  <p className="text-sm font-medium text-foreground mb-2">
                    Delivery Method <span className="text-red-500">*</span>
                  </p>
                  <p className="text-xs text-gray-500 mb-4">
                    The reset link will expire one hour after it has been sent
                  </p>
                </div>

                {/* Radio Options */}
                <div className="space-y-3">
                  {/* Email Option */}
                  <label className="flex items-center p-4 border-2 rounded-lg cursor-pointer transition-all hover:border-primary/50 has-[:checked]:border-primary has-[:checked]:bg-primary/5">
                    <input
                      type="radio"
                      value="email"
                      checked={sendMethod === 'email'}
                      onChange={() => setSendMethod('email')}
                      className="w-4 h-4 text-primary focus:ring-primary focus:ring-2"
                    />
                    <div className="ml-3 flex items-center gap-2">
                      <Mail size={18} className="text-primary" />
                      <span className="font-medium text-foreground">Send via Email</span>
                    </div>
                  </label>

                  {/* Text Option */}
                  <label className="flex items-center p-4 border-2 rounded-lg cursor-pointer transition-all hover:border-primary/50 has-[:checked]:border-primary has-[:checked]:bg-primary/5">
                    <input
                      type="radio"
                      value="text"
                      checked={sendMethod === 'text'}
                      onChange={() => setSendMethod('text')}
                      className="w-4 h-4 text-primary focus:ring-primary focus:ring-2"
                    />
                    <div className="ml-3 flex items-center gap-2">
                      <MessageSquare size={18} className="text-primary" />
                      <span className="font-medium text-foreground">Send via Text Message</span>
                    </div>
                  </label>
                </div>

                {/* Consent Message */}
                {sendMethod && (
                  <div className="mt-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                    <p className="text-sm text-gray-700">
                      {sendMethod === 'email' ? (
                        <>
                          By clicking <strong>Send Email</strong>, you agree to receive an email containing a link for resetting your password.
                        </>
                      ) : (
                        <>
                          By clicking <strong>Send Text</strong>, you agree to receive a text message from <strong>(800) 579-5808</strong>. The text message contains a link for resetting your password.
                        </>
                      )}
                    </p>
                  </div>
                )}
              </div>

              {/* Submit Button */}
              <Button
                type="submit"
                size="medium" variant="default" 
                disabled={status === 'loading'}
                className="w-full"
              >
                <Send size={20} />
                {status === 'loading'
                  ? 'Sending...'
                  : `Send${sendMethod ? ` ${sendMethod === 'email' ? 'Email' : 'Text'}` : ''}`
                }
              </Button>

              {/* Help Link */}
              <div className="text-center pt-4">
                <p className="text-sm text-gray-600">
                  Still having troubles?{' '}
                  <Link
                    to="/pub/contact_us"
                    className="text-primary font-medium hover:text-primary-hover hover:underline"
                  >
                    Contact us
                  </Link>
                </p>
              </div>
            </form>
          ) : (
            // Success state - show return to login
            <div className="text-center space-y-4">
              <Link to="/login">
                <Button size="medium" variant="default" >
                  Return to Login
                </Button>
              </Link>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="text-center mt-6">
          <Link
            to="/login"
            className="text-sm text-gray-600 hover:text-primary hover:underline"
          >
            Back to Login
          </Link>
        </div>
      </div>
    </div>
  );
};

export default ForgetPasswordPage;